/**
 * Update Proposal Dialog
 *
 * Customer-Detail entry point for replacing the active solar PV proposal.
 *
 * Flow:
 *  1. Admin selects a new proposal PDF.
 *  2. We POST /api/customers/{id}/update-proposal — Gemini extracts the new
 *     data, the customer's equipment fields are auto-applied, and the
 *     backend returns an `action` describing what happens next:
 *       - 'variation' (a completed contract exists) → open the existing
 *         VariationOfContractDialog (pre-fills against the agreed snapshot).
 *       - 'replace'   (an in-flight contract exists, not complete) → confirm
 *         "Replace the pending contract?" and on yes call replace-pending
 *         then open the Contract Pack Wizard.
 *       - 'new'       (no contract on file or all hard-deleted) → open the
 *         Contract Pack Wizard directly.
 *  3. Before the action prompt we show a side-by-side diff of what changed
 *     in the new proposal vs the previous one (proposal_diff).
 */
import React, { useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Upload, Loader2, FileSignature, FileText, AlertTriangle, CheckCircle2,
  RefreshCw, RotateCcw, ArrowRight, FilePlus2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { runProposalJob } from '../lib/proposalJob';

const KIND_TONE = {
  added: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  removed: 'bg-rose-50 text-rose-700 border-rose-200',
  changed: 'bg-amber-50 text-amber-700 border-amber-200',
};

const HUMAN_FIELD = {
  installed_capacity_kwp: 'System size (kWp)',
  estimated_annual_output_kwh: 'Estimated annual output (kWh)',
  battery_usable_capacity_kwh: 'Battery capacity (kWh)',
  battery_manufacturer: 'Battery — manufacturer',
  battery_model: 'Battery — model',
  panel_manufacturer: 'Panel — manufacturer',
  panel_model: 'Panel — model',
  panel_count: 'Panel — count',
  panel_wattage_w: 'Panel — wattage (W)',
  inverter_manufacturer: 'Inverter — manufacturer',
  inverter_model: 'Inverter — model',
  inverter_rating_kw: 'Inverter — AC rating (kW)',
  customer_name: 'Customer name',
  customer_address: 'Customer address',
  postcode: 'Postcode',
  mpan: 'MPAN',
  orientation_degrees_from_south: 'Orientation (° from south)',
  inclination_degrees_from_horizontal: 'Inclination (°)',
  kwh_per_kwp_kk: 'kWh/kWp (Kk)',
  shade_factor: 'Shade factor',
  occupancy_archetype: 'Occupancy archetype',
  annual_electricity_consumption_kwh: 'Annual consumption (kWh)',
  expected_self_consumption_pv_only_kwh: 'Self-consumption — PV only',
  grid_independence_pv_only_percent: 'Grid independence — PV only (%)',
  expected_self_consumption_with_eess_kwh: 'Self-consumption — with EESS',
  grid_independence_with_eess_percent: 'Grid independence — with EESS (%)',
  eess_capacity_not_used_kwh: 'EESS capacity not used',
  total_energy_discharged_kwh: 'Total energy discharged',
  additional_self_consumption_kwh: 'Additional self-consumption',
  postcode_region: 'Postcode region',
};

const fmtValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? v : v.toFixed(2);
  return String(v);
};

export const UpdateProposalDialog = ({
  open, onOpenChange, customer, onApplied,
  onOpenVariation, onOpenContractWizard,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // backend response
  const [replacing, setReplacing] = useState(false);

  const reset = () => {
    setResult(null);
    setUploading(false);
    setReplacing(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF proposal');
      try { e.target.value = ''; } catch { /* noop */ }
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Proposal PDF exceeds 30 MB');
      try { e.target.value = ''; } catch { /* noop */ }
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const result = await runProposalJob(api, `/customers/${customer.id}/update-proposal/start`, fd);
      setResult(result || {});
      const n = (result?.proposal_diff || []).length;
      const m = Object.keys(result?.applied_fields || {}).length;
      toast.success(
        `Proposal updated — ${n} field${n === 1 ? '' : 's'} changed, ${m} customer field${m === 1 ? '' : 's'} auto-applied.`,
        { duration: 5000 },
      );
      onApplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Proposal update failed');
    } finally {
      setUploading(false);
      try { e.target.value = ''; } catch { /* noop */ }
    }
  };

  const handleReplace = async () => {
    if (!result?.pending_packs?.length) return;
    if (!window.confirm(
      `Hard-delete ${result.pending_packs.length} in-flight contract pack(s) and open a fresh Contract Pack Wizard?`,
    )) return;
    setReplacing(true);
    try {
      await api.post(`/customers/${customer.id}/contract-packs/replace-pending`);
      toast.success('Pending contract(s) deleted — opening Contract Pack Wizard');
      close();
      onOpenContractWizard?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replace failed');
    } finally {
      setReplacing(false);
    }
  };

  const handleVariation = () => {
    close();
    onOpenVariation?.();
  };

  const handleNew = () => {
    close();
    onOpenContractWizard?.();
  };

  const action = result?.action;
  const diff = result?.proposal_diff || [];
  const applied = result?.applied_fields || {};

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-3xl" data-testid="update-proposal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-amber-600" />
            Update Solar PV Proposal
          </DialogTitle>
          <DialogDescription>
            Upload the customer's revised proposal. We'll extract the new figures, auto-apply them to the
            customer record, and route to the correct next step based on the contract status.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="py-6 space-y-3">
            <div className="rounded-md border border-dashed border-amber-300 bg-amber-50/60 p-6 text-center">
              <FileText className="w-10 h-10 mx-auto text-amber-500 mb-2" />
              <p className="text-sm text-slate-600 mb-3">
                Select the new proposal PDF (≤ 30 MB). The previous proposal is kept on the customer file as <em>Superseded</em>.
              </p>
              <Button
                type="button"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="update-proposal-upload-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reading proposal…</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Upload new proposal PDF</>
                )}
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleFile}
                className="hidden"
                data-testid="update-proposal-input"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {/* Action banner */}
            {action === 'variation' && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 flex items-start gap-2" data-testid="action-banner-variation">
                <FileSignature className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <p className="font-semibold">A signed contract exists — Variation of Contract required.</p>
                  <p className="mt-0.5">We'll open the Variation form with the equipment diff already populated.</p>
                </div>
              </div>
            )}
            {action === 'replace' && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="action-banner-replace">
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold">
                    {result.pending_packs.length} in-flight contract pack(s) detected — needs replacing.
                  </p>
                  <p className="mt-0.5">
                    The contract isn't complete yet. Click <strong>Replace pending contract</strong> below to hard-delete the in-flight pack(s) and open a fresh Contract Pack Wizard.
                  </p>
                </div>
              </div>
            )}
            {action === 'new' && (
              <div className="rounded-md border border-sky-300 bg-sky-50 p-3 flex items-start gap-2" data-testid="action-banner-new">
                <FilePlus2 className="w-5 h-5 text-sky-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-sky-900">
                  <p className="font-semibold">No active contract pack on file.</p>
                  <p className="mt-0.5">Open the Contract Pack Wizard to generate a fresh one with the updated equipment.</p>
                </div>
              </div>
            )}

            {/* Applied fields summary */}
            {Object.keys(applied).length > 0 && (
              <div className="rounded-md border border-slate-200 bg-white p-3">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
                  Auto-applied to customer record
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(applied).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800 text-[11px]">
                      {k.replace(/_/g, ' ')}: <strong className="ml-1">{fmtValue(v)}</strong>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Diff table */}
            {diff.length === 0 ? (
              <div className="text-sm text-slate-500 italic py-3 text-center" data-testid="no-diff">
                The new proposal matches the previous one — no field changes detected.
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Field</th>
                      <th className="text-left px-3 py-2 font-medium">Previous</th>
                      <th className="text-left px-3 py-2 font-medium">New</th>
                      <th className="text-left px-3 py-2 font-medium">Change</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-testid="diff-table-body">
                    {diff.map((row) => (
                      <tr key={row.field}>
                        <td className="px-3 py-2 text-slate-700">{HUMAN_FIELD[row.field] || row.field}</td>
                        <td className="px-3 py-2 text-slate-500">{fmtValue(row.prev)}</td>
                        <td className="px-3 py-2 text-slate-800 font-medium">{fmtValue(row.curr)}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={`${KIND_TONE[row.kind]} text-[11px] capitalize`}>
                            {row.kind}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={close} disabled={uploading || replacing} data-testid="update-proposal-cancel">
            {result ? 'Close' : 'Cancel'}
          </Button>
          {result && (
            <>
              <Button
                variant="outline"
                onClick={reset}
                disabled={uploading || replacing}
                data-testid="update-proposal-reupload"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> Upload a different PDF
              </Button>
              {action === 'variation' && (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={handleVariation}
                  data-testid="update-proposal-open-variation"
                >
                  <FileSignature className="w-4 h-4 mr-1.5" /> Open Variation of Contract
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              )}
              {action === 'replace' && (
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleReplace}
                  disabled={replacing}
                  data-testid="update-proposal-replace"
                >
                  {replacing ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Replacing…</>
                  ) : (
                    <><AlertTriangle className="w-4 h-4 mr-1.5" /> Replace pending contract</>
                  )}
                </Button>
              )}
              {action === 'new' && (
                <Button
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  onClick={handleNew}
                  data-testid="update-proposal-open-wizard"
                >
                  <FilePlus2 className="w-4 h-4 mr-1.5" /> Open Contract Pack Wizard
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateProposalDialog;
